/**
 * Think & Tidy site-wide behavior: nav burger toggle, scroll-spy, scroll-
 * reveal, sticky mobile buy bar, quantity stepper, add-to-cart — runs on
 * every page (not just the homepage/PDP) so the shared nav/footer chrome
 * works everywhere. Variant image/price swap only runs on pages that emit
 * a #tdw-variant-data block (product pages). Vanilla JS, no framework, no
 * dependency on Horizon's component bundle.
 */
(function () {
  'use strict';

  /**
   * Derive prefix/suffix from a rendered example (Liquid renders 100000000 =
   * "Rs 1,000,000" or similar) so JS-driven price formatting respects the
   * shop's real money format instead of a hardcoded currency string. Lives
   * on a site-wide hidden element (not just the PDP's variant-data block)
   * so pages with no product context — like the wishlist grid — can use it.
   */
  var moneyFormatEl = document.getElementById('tdw-money-format');
  var moneyTemplate = (moneyFormatEl && moneyFormatEl.dataset.format) || '{{amount}}';
  var moneyMatch = moneyTemplate.match(/^(.*?)[\d.,]*000[\d.,]*(.*)$/);
  var moneyPrefix = moneyMatch ? moneyMatch[1] : '';
  var moneySuffix = moneyMatch ? moneyMatch[2] : '';

  function formatMoney(cents) {
    var amount = Math.round(cents / 100);
    var withCommas = String(amount).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return moneyPrefix + withCommas + moneySuffix;
  }

  var dataEl = document.getElementById('tdw-variant-data');
  var variants = [];
  if (dataEl) {
    try { variants = JSON.parse(dataEl.textContent); } catch (e) { variants = []; }
  }

  var state = {
    variantId: variants.length ? variants[0].id : null,
  };

  function findVariant(id) {
    for (var i = 0; i < variants.length; i++) {
      if (String(variants[i].id) === String(id)) return variants[i];
    }
    return variants[0];
  }

  if (variants.length) {
    var applyVariant = function (id, opts) {
      var v = findVariant(id);
      if (!v) return;
      state.variantId = v.id;
      opts = opts || {};

      document.querySelectorAll('[data-tdw-swatch]').forEach(function (el) {
        el.classList.toggle('is-selected', String(el.dataset.tdwSwatch) === String(v.id));
        el.setAttribute('aria-pressed', String(el.dataset.tdwSwatch) === String(v.id));
      });

      document.querySelectorAll('[data-tdw-hero-image]').forEach(function (img) {
        if (v.image) { img.src = v.image; img.alt = v.title; }
      });
      document.querySelectorAll('[data-tdw-color-name]').forEach(function (el) { el.textContent = v.title; });

      document.querySelectorAll('[data-tdw-price]').forEach(function (el) { el.textContent = formatMoney(v.price); });
      document.querySelectorAll('[data-tdw-compare-price]').forEach(function (el) {
        if (v.compareAtPrice && v.compareAtPrice > v.price) {
          el.textContent = formatMoney(v.compareAtPrice);
          el.style.display = '';
        } else {
          el.style.display = 'none';
        }
      });
      document.querySelectorAll('[data-tdw-discount-badge]').forEach(function (el) {
        var pctEl = el.querySelector('[data-tdw-discount-pct]');
        if (v.compareAtPrice && v.compareAtPrice > v.price) {
          var pct = Math.round(((v.compareAtPrice - v.price) / v.compareAtPrice) * 100);
          if (pctEl) pctEl.textContent = pct;
          el.style.display = '';
        } else {
          el.style.display = 'none';
        }
      });
      document.querySelectorAll('[data-tdw-variant-input]').forEach(function (el) { el.value = v.id; });

      if (!opts.silent) {
        document.querySelectorAll('[data-tdw-swatch]').forEach(function (el) {
          if (String(el.dataset.tdwSwatch) === String(v.id)) el.focus({ preventScroll: true });
        });
      }
    };

    document.addEventListener('click', function (e) {
      var trigger = e.target.closest('[data-tdw-swatch]');
      if (!trigger) return;
      applyVariant(trigger.dataset.tdwSwatch);
    });

    applyVariant(state.variantId, { silent: true });
  }

  /* ---------------- Quantity stepper ---------------- */
  function clampQty(input) {
    var min = parseInt(input.min, 10) || 1;
    var max = input.max !== '' ? parseInt(input.max, 10) : NaN;
    var val = parseInt(input.value, 10);
    if (isNaN(val) || val < min) val = min;
    if (!isNaN(max) && val > max) val = max;
    input.value = val;
    return val;
  }

  document.addEventListener('click', function (e) {
    var incBtn = e.target.closest('[data-tdw-qty-increment]');
    var decBtn = e.target.closest('[data-tdw-qty-decrement]');
    if (!incBtn && !decBtn) return;
    var wrap = (incBtn || decBtn).closest('[data-tdw-qty-wrap]');
    var input = wrap && wrap.querySelector('[data-tdw-qty]');
    if (!input) return;
    var val = clampQty(input);
    var min = parseInt(input.min, 10) || 1;
    var max = input.max !== '' ? parseInt(input.max, 10) : NaN;
    var next = incBtn ? val + 1 : Math.max(min, val - 1);
    if (!isNaN(max) && next > max) next = max;
    input.value = next;
  });

  document.addEventListener('change', function (e) {
    var input = e.target.closest('[data-tdw-qty]');
    if (!input) return;
    clampQty(input);
  });

  /* ---------------- Buy Now (standard "express checkout" behaviour) ----------------
     What the big stores do (Amazon, Daraz, Shopify's own Buy-it-now): Buy Now
     checks out ONLY the product on screen, in the chosen quantity, in its own
     separate checkout, and the shopper's basket (the Add to Cart cart) is
     left completely alone.

     PRIMARY PATH — Storefront API cartCreate. A brand-new one-line cart is
     created for this product + quantity and the shopper is sent to that
     cart's own checkoutUrl. The cookie cart (the theme's cart, the header
     count, the cart page) is never touched, so there is nothing to clear or
     restore, and no leftover/duplicated items can ever reach checkout.
     The token below is the PUBLIC Storefront access token of the "Think&Tidy
     Headless" storefront (scopes: unauthenticated_read_product_listings,
     unauthenticated_write_checkouts, unauthenticated_read_checkouts). Public
     tokens are designed to ship in browser code; never put the private
     (shpat_) token here. Products must stay published to the Headless
     channel or cartCreate returns no cart (the fallback then kicks in).
     API version 2025-07 is supported for 12 months from its release; bump
     SF_VERSION when Shopify retires it.

     FALLBACK — if the Storefront call fails for any reason (offline blip,
     product not on the channel, version retired) the previous method runs so
     checkout is never blocked: snapshot the cookie cart to localStorage,
     empty it, add just this product, go to /checkout, and put the snapshot
     back on the next storefront page load (cart holds only the Buy Now item
     -> replaced by the snapshot; shopper added other things since -> keep
     those and add only the missing snapshot items; older than 14 days ->
     discarded). */
  var SF_TOKEN = '371f1778d43d7d52e594c515c48e7ae0';
  var SF_VERSION = '2025-07';
  var SF_URL = '/api/' + SF_VERSION + '/graphql.json';

  var STASH_KEY = 'tdw_buynow_stash';
  var STASH_TTL = 14 * 24 * 3600 * 1000;
  var CART_ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var buyNowBusy = false;

  /* ---- primary: separate one-item checkout ---- */
  function createExpressCheckout(variantId, quantity) {
    return fetch(SF_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': SF_TOKEN,
      },
      body: JSON.stringify({
        query: 'mutation($l:[CartLineInput!]!){cartCreate(input:{lines:$l}){cart{checkoutUrl} userErrors{message}}}',
        variables: { l: [{ merchandiseId: 'gid://shopify/ProductVariant/' + variantId, quantity: quantity }] },
      }),
    })
      .then(function (r) { return r.json(); })
      .then(function (j) {
        var d = j && j.data && j.data.cartCreate;
        var url = d && d.cart && d.cart.checkoutUrl;
        if (!url || (d.userErrors && d.userErrors.length)) throw new Error('no express checkout');
        return url;
      });
  }

  /* ---- fallback helpers: snapshot / restore the cookie cart ---- */
  function readStash() {
    try {
      var s = JSON.parse(window.localStorage.getItem(STASH_KEY));
      if (!s || !Array.isArray(s.items)) return null;
      if (Date.now() - (s.ts || 0) > STASH_TTL) { window.localStorage.removeItem(STASH_KEY); return null; }
      return s;
    } catch (e) { return null; }
  }
  function writeStash(s) { try { window.localStorage.setItem(STASH_KEY, JSON.stringify(s)); } catch (e) { /* storage unavailable */ } }
  function dropStash() { try { window.localStorage.removeItem(STASH_KEY); } catch (e) { /* storage unavailable */ } }

  function getCart() {
    return fetch(CART_ROOT + 'cart.js', { headers: { Accept: 'application/json' } }).then(function (r) { return r.json(); });
  }
  function clearCart() {
    return fetch(CART_ROOT + 'cart/clear.js', { method: 'POST', headers: { Accept: 'application/json' } });
  }
  function addItems(items) {
    if (!items.length) return Promise.resolve();
    return fetch(CART_ROOT + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: items }),
    }).then(function (res) {
      if (res.ok) return;
      /* One unavailable item rejects the whole batch: add the rest one by one. */
      return items.reduce(function (p, it) {
        return p.then(function () {
          return fetch(CART_ROOT + 'cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items: [it] }),
          }).catch(function () { /* skip */ });
        });
      }, Promise.resolve());
    });
  }

  function paintCartCount(n) {
    var link = document.querySelector('a.tdw2-nav__icon[aria-label="Cart"]');
    if (!link) return;
    var badge = link.querySelector('.count');
    if (!badge && n > 0) {
      badge = document.createElement('span');
      badge.className = 'count';
      link.appendChild(badge);
    }
    if (!badge) return;
    badge.textContent = n;
    badge.style.display = n > 0 ? '' : 'none';
  }

  function restoreStash() {
    var s = readStash();
    if (!s) return Promise.resolve();
    var items = s.items.map(function (i) {
      return { id: i.id, quantity: i.quantity, properties: i.properties || undefined };
    });
    return getCart()
      .then(function (cart) {
        var buyId = String(s.buyId);
        var onlyBuyLine = cart.items.every(function (i) { return String(i.variant_id) === buyId; });
        if (onlyBuyLine) {
          return clearCart().then(function () { return addItems(items); });
        }
        var have = {};
        cart.items.forEach(function (i) { have[String(i.variant_id)] = true; });
        return addItems(items.filter(function (i) { return !have[String(i.id)]; }));
      })
      .then(function () { dropStash(); return getCart(); })
      .then(function (cart) { paintCartCount(cart.item_count); })
      .catch(function () { /* leave the cart as it is */ });
  }

  function legacyBuyNow(variantId, quantity) {
    return restoreStash()
      .then(getCart)
      .then(function (cart) {
        var saved = cart.items.map(function (i) {
          return { id: i.variant_id, quantity: i.quantity, properties: i.properties || undefined };
        });
        if (saved.length) writeStash({ ts: Date.now(), buyId: variantId, items: saved });
        return clearCart();
      })
      .then(function () {
        return fetch(CART_ROOT + 'cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: variantId, quantity: quantity }] }),
        });
      })
      .then(function (res) {
        if (!res.ok) return res.json().then(function (err) { throw err; });
        return res.json();
      })
      .then(function () { return '/checkout'; });
  }

  document.addEventListener('submit', function (e) {
    var form = e.target.closest('[data-tdw-form]');
    if (!form) return;
    e.preventDefault();
    if (buyNowBusy) return; /* ignore double taps */
    buyNowBusy = true;

    var btn = form.querySelector('[data-tdw-submit]');
    if (btn) { btn.classList.add('is-loading'); btn.disabled = true; }

    var qtyInput = form.querySelector('[data-tdw-qty]');
    var quantity = qtyInput ? clampQty(qtyInput) : 1;
    var variantInput = form.querySelector('[data-tdw-variant-input]');
    var variantId = variantInput ? variantInput.value : state.variantId;

    createExpressCheckout(variantId, quantity)
      .catch(function () { return legacyBuyNow(variantId, quantity); })
      .then(function (url) {
        window.location.href = url;
      })
      .catch(function () {
        buyNowBusy = false;
        if (btn) { btn.classList.remove('is-loading'); btn.disabled = false; }
        /* A snapshot (if any) is put back when the cart page loads. */
        window.location.href = '/cart';
      });
  });

  /* Coming back with the browser's Back button (bfcache) revives the page
     with the Buy Now button still in its loading state: reset it (and put
     back a fallback snapshot if one exists). */
  window.addEventListener('pageshow', function (e) {
    if (!e.persisted) return;
    buyNowBusy = false;
    document.querySelectorAll('[data-tdw-submit]').forEach(function (b) {
      b.classList.remove('is-loading');
      if (!b.dataset.tdwKeepDisabled) b.disabled = false;
    });
    restoreStash();
  });

  /* Put a fallback-saved basket back on any storefront page the shopper lands on. */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { restoreStash(); });
  } else {
    restoreStash();
  }

  /* ---------------- Scroll-spy nav ---------------- */
  var navLinks = Array.prototype.slice.call(document.querySelectorAll('[data-tdw-nav-link]'));
  var spySections = navLinks
    .map(function (link) {
      var id = link.getAttribute('href').replace('#', '');
      var section = document.getElementById(id);
      return section ? { link: link, section: section } : null;
    })
    .filter(Boolean);

  if (spySections.length && 'IntersectionObserver' in window) {
    var spyObserver = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          var match = spySections.find(function (s) { return s.section === entry.target; });
          if (!match) return;
          if (entry.isIntersecting) {
            navLinks.forEach(function (l) { l.classList.remove('is-active'); });
            match.link.classList.add('is-active');
          }
        });
      },
      { rootMargin: '-45% 0px -50% 0px', threshold: 0 }
    );
    spySections.forEach(function (s) { spyObserver.observe(s.section); });
  }

  /* ---------------- Scroll-reveal ---------------- */
  var revealEls = document.querySelectorAll('[data-tdw-reveal]');
  if (revealEls.length && 'IntersectionObserver' in window) {
    var revealObserver = new IntersectionObserver(
      function (entries, obs) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            obs.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -40px 0px' }
    );
    revealEls.forEach(function (el) { revealObserver.observe(el); });
  } else {
    revealEls.forEach(function (el) { el.classList.add('is-visible'); });
  }

  /* ---------------- Sticky mobile buy bar ---------------- */
  var stickyBar = document.querySelector('[data-tdw-sticky-bar]');
  var heroEl = document.getElementById('overview');
  if (stickyBar && heroEl && 'IntersectionObserver' in window) {
    var barObserver = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          var show = !entry.isIntersecting && entry.boundingClientRect.top < 0;
          stickyBar.classList.toggle('is-visible', show);
          document.body.classList.toggle('tdw-has-sticky-bar', show);
        });
      },
      { threshold: 0 }
    );
    barObserver.observe(heroEl);
  }

  /* ---------------- Mobile nav toggle ---------------- */
  var navEl = document.querySelector('[data-tdw-nav]');
  var burger = document.querySelector('[data-tdw-nav-burger]');
  if (navEl && burger) {
    burger.addEventListener('click', function () {
      var open = navEl.classList.toggle('is-menu-open');
      burger.setAttribute('aria-expanded', String(open));
    });
    navEl.querySelectorAll('[data-tdw-nav-link]').forEach(function (link) {
      link.addEventListener('click', function () {
        navEl.classList.remove('is-menu-open');
        burger.setAttribute('aria-expanded', 'false');
      });
    });
  }

  /* ---------------- Wishlist (client-side, localStorage) ---------------- */
  var WISHLIST_KEY = 'tdw_wishlist';

  function getWishlist() {
    try {
      var arr = JSON.parse(window.localStorage.getItem(WISHLIST_KEY) || '[]');
      return Array.isArray(arr) ? arr : [];
    } catch (e) {
      return [];
    }
  }

  function setWishlist(arr) {
    try { window.localStorage.setItem(WISHLIST_KEY, JSON.stringify(arr)); } catch (e) { /* storage unavailable */ }
    document.dispatchEvent(new CustomEvent('tdw:wishlist-change'));
  }

  function toggleWish(handle) {
    var list = getWishlist();
    var idx = list.indexOf(handle);
    if (idx === -1) { list.push(handle); } else { list.splice(idx, 1); }
    setWishlist(list);
  }

  function syncWishlistButtons() {
    var list = getWishlist();
    document.querySelectorAll('[data-tdw-wishlist-toggle]').forEach(function (btn) {
      var wished = list.indexOf(btn.dataset.productHandle) !== -1;
      btn.classList.toggle('is-active', wished);
      btn.setAttribute('aria-pressed', String(wished));
      btn.setAttribute('aria-label', wished ? 'Remove from wishlist' : 'Add to wishlist');
    });
    document.querySelectorAll('[data-tdw-wishlist-count]').forEach(function (el) {
      el.textContent = list.length;
      el.style.display = list.length > 0 ? '' : 'none';
    });
  }

  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-tdw-wishlist-toggle]');
    if (!btn || !btn.dataset.productHandle) return;
    e.preventDefault();
    e.stopPropagation();
    toggleWish(btn.dataset.productHandle);
  });

  document.addEventListener('tdw:wishlist-change', syncWishlistButtons);
  syncWishlistButtons();

  /* ---------------- Wishlist page: fetch + render saved products ---------------- */
  var wishlistGrid = document.querySelector('[data-tdw-wishlist-grid]');
  if (wishlistGrid) {
    var wishlistEmptyEl = document.querySelector('[data-tdw-wishlist-empty]');
    var heartIconPath = 'M12 21s-7-4.35-9.5-8.5C.7 8.9 2.4 5 6 5c2 0 3.5 1.2 4 2.5C10.5 6.2 12 5 14 5c3.6 0 5.3 3.9 3.5 7.5C15 16.65 12 21 12 21z';

    function renderWishlistCard(p) {
      var img = p.featured_image || (p.images && p.images[0]) || '';
      var priceHtml = '<span class="now">' + formatMoney(p.price) + '</span>';
      if (p.compare_at_price && p.compare_at_price > p.price) {
        priceHtml += '<span class="was">' + formatMoney(p.compare_at_price) + '</span>';
      }
      var a = document.createElement('a');
      a.href = p.url || '/products/' + p.handle;
      a.className = 'tdw2-card';
      a.innerHTML =
        '<div class="tdw2-card__figure">' +
          '<button type="button" class="tdw2-wishlist-btn is-active" data-tdw-wishlist-toggle data-product-handle="' + p.handle + '" aria-label="Remove from wishlist">' +
            '<svg width="16" height="16" viewBox="0 0 24 24" stroke-width="1.8"><path d="' + heartIconPath + '"/></svg>' +
          '</button>' +
          (img ? '<img src="' + img + '" alt="' + String(p.title).replace(/"/g, '&quot;') + '" width="600" height="600" loading="lazy">' : '') +
        '</div>' +
        '<div class="tdw2-card__body">' +
          '<div class="tdw2-card__vendor">' + (p.type || p.product_type || '') + '</div>' +
          '<div class="tdw2-card__title">' + p.title + '</div>' +
          '<div class="tdw2-card__price">' + priceHtml + '</div>' +
        '</div>';
      return a;
    }

    function loadWishlistPage() {
      var handles = getWishlist();
      wishlistGrid.innerHTML = '';
      if (!handles.length) {
        if (wishlistEmptyEl) wishlistEmptyEl.hidden = false;
        return;
      }
      Promise.all(
        handles.map(function (handle) {
          return fetch('/products/' + handle + '.js')
            .then(function (res) { return res.ok ? res.json() : null; })
            .catch(function () { return null; });
        })
      ).then(function (products) {
        var valid = products.filter(Boolean);
        if (wishlistEmptyEl) wishlistEmptyEl.hidden = valid.length > 0;
        valid.forEach(function (p) { wishlistGrid.appendChild(renderWishlistCard(p)); });
      });
    }

    loadWishlistPage();
    document.addEventListener('tdw:wishlist-change', loadWishlistPage);
  }
})();
